import { Suspense, useState } from 'react';
import { HoverCard, Skeleton } from '@mantine/core';
import {
  DateStringValue,
  MonthView,
  ScheduleEventData,
} from '@mantine/schedule';
import dayjs from 'dayjs';
import { ScheduleEvent, TouringScheduleHeader } from './components';
import { EVENT_TYPE_OPTIONS } from './data';
import { getEventsCountByDay } from './helpers';
import { useTouringScheduleEvents } from './hooks';
import type { EventType, PayloadType } from './types';
import classes from './TouringSchedule.module.css';

const TouringScheduleContent: React.FC = () => {
  const [date, setDate] = useState<DateStringValue>(
    dayjs().format('YYYY-MM-DD')
  );
  const [filter, setFilter] = useState<EventType[]>(EVENT_TYPE_OPTIONS);

  const { events } = useTouringScheduleEvents({ date, filter });
  const eventsCountByDay = getEventsCountByDay(events);
  const maxEventsPerDay = Math.max(2, ...Object.values(eventsCountByDay));

  return (
    <HoverCard.Group openDelay={0} closeDelay={200}>
      <TouringScheduleHeader
        date={date}
        setDate={setDate}
        filter={filter}
        setFilter={setFilter}
      />
      <MonthView
        date={date}
        onDateChange={setDate}
        withHeader={false}
        events={events}
        maxEventsPerDay={maxEventsPerDay}
        withWeekNumbers={true}
        classNames={{
          monthViewDay: classes.monthViewDay,
          monthViewEvents: classes.monthViewEvents,
          monthViewWeekdaysCorner: classes.monthViewWeekdaysCorner,
          monthViewScrollArea: classes.monthViewScrollArea,
        }}
        scrollAreaProps={{ scrollbarSize: 0 }}
        getDayProps={(dayDate) => ({
          style: {
            '--day-events-count': eventsCountByDay[dayDate] ?? 0,
            cursor: 'default',
            background: 'unset',
          },
        })}
        renderEvent={(event, props) => (
          <ScheduleEvent
            event={event as ScheduleEventData<PayloadType>}
            {...props}
          />
        )}
      />
    </HoverCard.Group>
  );
};

export const TouringSchedule: React.FC = () => {
  return (
    <Suspense fallback={<Skeleton height={480} />}>
      <TouringScheduleContent />
    </Suspense>
  );
};
